import {matchRoutes} from 'react-router'
import find from 'lodash/find'
import {exhaustive} from '@freckle/exhaustive'
import {logError} from '@freckle/classroom/ts/common/helpers/exception-handlers/bugsnag-client'

export type SidebarIdT =
  | 'dashboard-tab'
  | 'activity-feed-tab'
  | 'assignments-tab'
  | 'rosters-tab'
  | 'common-reports-tab'
  | 'get-premium-tab'
  | 'math-tab'
  | 'ela-tab'
  | 'social-studies-tab'
  | 'science-tab'
  | 'none'

type SidebarIdsT = {
  Dashboard: SidebarIdT
  ActivityFeed: SidebarIdT
  Assignments: SidebarIdT
  Reports: SidebarIdT
  Rosters: SidebarIdT
  GetPremium: SidebarIdT
  MathContent: SidebarIdT
  ElaContent: SidebarIdT
  SocialStudiesUnits: SidebarIdT
  ScienceUnits: SidebarIdT
  None: SidebarIdT
}

const SidebarIds: SidebarIdsT = {
  Dashboard: 'dashboard-tab',
  ActivityFeed: 'activity-feed-tab',
  Assignments: 'assignments-tab',
  Reports: 'common-reports-tab',
  Rosters: 'rosters-tab',
  GetPremium: 'get-premium-tab',
  MathContent: 'math-tab',
  ElaContent: 'ela-tab',
  SocialStudiesUnits: 'social-studies-tab',
  ScienceUnits: 'science-tab',

  //for routes like teacher-onboarding that do not use the sidebar.
  None: 'none'
}

export function getDataTestFromTabId(tabId: SidebarIdT): string {
  switch (tabId) {
    case 'dashboard-tab':
      return 'sidebar-link-dashboard'
    case 'activity-feed-tab':
      return 'sidebar-link-activity-feed'
    case 'assignments-tab':
      return 'sidebar-link-assignments'
    case 'common-reports-tab':
      return 'sidebar-link-reports'
    case 'rosters-tab':
      return 'sidebar-link-roster'
    case 'get-premium-tab':
      return 'sidebar-link-get-premium'
    case 'math-tab':
      return 'sidebar-link-math'
    case 'ela-tab':
      return 'sidebar-link-ela'
    case 'social-studies-tab':
      return 'sidebar-link-social-studies'
    case 'science-tab':
      return 'sidebar-link-science'
    case 'none':
      return 'sidebar-link-none'

    default:
      return exhaustive(tabId, 'getDataTestFromTabId')
  }
}

export function getSidebarLinkTranslateKeyFromTabId(tabId: SidebarIdT): string {
  switch (tabId) {
    case 'dashboard-tab':
      return 'SIDEBAR_DASHBOARD'
    case 'activity-feed-tab':
      return 'SIDEBAR_ACTIVITY_FEED'
    case 'assignments-tab':
      return 'SIDEBAR_ASSIGNMENTS'
    case 'common-reports-tab':
      return 'SIDEBAR_COMMON_REPORTS'
    case 'rosters-tab':
      return 'SIDEBAR_ROSTER'
    case 'get-premium-tab':
      return 'SIDEBAR_GET_PREMIUM'
    case 'math-tab':
      return 'SIDEBAR_MATH'
    case 'ela-tab':
      return 'SIDEBAR_ELA'
    case 'social-studies-tab':
      return 'SIDEBAR_SOCIAL_STUDIES'
    case 'science-tab':
      return 'SIDEBAR_SCIENCE'
    case 'none':
      return ''
    default:
      return exhaustive(tabId)
  }
}

const routes = [
  {
    paths: ['/dashboard', '/start'],
    tab: SidebarIds.Dashboard
  },
  {
    paths: ['/activity-feed', '/ela/reports/focus-skills/:assignmentId/students/:studentId'],
    tab: SidebarIds.ActivityFeed
  },
  {
    paths: [
      '/assignments',
      '/math/assignments',
      '/ela/assignments',
      '/ela/targeted-skill-practice/assign',
      '/assignments/adaptive',
      '/assignments/number-facts',
      '/assignments/targeted',
      '/assignments/assessments',
      '/assignments/fact-practice',
      '/assignments/targeted-depth-of-knowledge-practice',
      '/assignments/math-focus-skills-practice',
      '/assignments/ela-focus-skills-practice',
      '/science/assignments',
      '/social-studies/assignments',
      '/math/targeted-session/:assignmentSessionId/report',
      '/math/focus-skills-practice-session/:assignmentSessionId/report',
      '/ela/reports/targeted-skills/:assignmentId/students/:studentId'
    ],
    tab: SidebarIds.Assignments
  },
  {
    paths: [
      '/student-goals',
      '/ela/student-goals',
      '/ela/report-cards',
      '/ela/report-cards/:studentId',
      '/ela/reports',
      '/ela/reports/performance-by-standard',
      '/ela/groups-report',
      '/session-history/ela-article',
      '/ela/reports/levels',
      '/ela/reports/assessments',
      '/ela/word-study-matrix',
      '/ela-skills-progress-report',
      '/math-skills-progress-report',
      '/math-diagnostic-report',
      '/math-adaptive-report',
      '/math-report-card',
      '/math-standards-report',
      '/ela/report-card',
      '/class-grouping-report',
      '/math-assessment-growth',
      '/math/reports',
      '/fact-practice-reports',
      '/fact-practice-report/students/:studentId/operations/:operation',
      '/report-card-summaries',
      '/science/reports',
      '/social-studies/reports',
      '/reports-overview'
    ],
    tab: SidebarIds.Reports
  },
  {
    paths: [
      '/ela/articles/:articleUuid/assign',
      '/ela/assessments',
      '/ela/word-study',
      '/ela/decodable/assign',
      '/ela/articles',
      '/ela/articles/:articleUuid',
      '/ela/articles/:articleUuid',
      '/ela/standards',
      '/ela/standards/:elaDomainCode/:elaStandardCode',
      '/ela/overview',
      '/ela/overview',
      '/ela/standards/students/:studentId/report',
      '/ela/print-ela-report-cards',
      '/ela/reports/assessment/:assignmentId',
      '/ela/reports/assessment-sessions/:assignmentSessionId',
      '/ela/reports/writing/:assignmentId',
      '/ela/reports/reading/:assignmentId/students/:studentId',
      '/ela/reports/adaptive-skills/:assignmentId/students/:studentId',
      '/ela/reports/adaptive-grammar/:assignmentId/students/:studentId',
      '/ela/reports/targeted-grammar/:assignmentId/students/:studentId',
      '/ela/reports/word-study/:assignmentSessionId/students/:studentId',
      '/ela/reports/targeted-skills-assignment/:assignmentId',
      '/reports/ela-focus-skills-practice/:assignmentId',
      '/session-history/ela-article/:articleUuid',
      '/ela/correlation'
    ],
    tab: SidebarIds.ElaContent
  },
  {
    paths: [
      '/session-history/math-assessment-session/:sessionId',
      '/session-history/math-assessment/:assessmentId/session/:sessionId',
      '/math/assessment-session/:sessionId/report',
      '/assessment/:assessmentId/report',
      '/math/standards/students/:studentId/report',
      '/math/goals/fact-fluency-overview/',
      '/math/adaptive-session/:assignmentSessionId/report',
      '/standards',
      '/standards/:standardId',
      '/print-math-report-cards',
      '/lessons',
      '/printables',
      '/lessons/:lessonId',
      '/lessons/:lessonId/days/:dayNum/slideshow',
      '/lessons/:lessonId/assignments',
      '/constructed-responses',
      '/math-assessment-growth-detailed',
      '/math/reports/targeted-practice/:assignmentId',
      '/math/reports/dok-assignment/:assignmentId',
      '/reports/math-focus-skills-practice/:assignmentId',
      '/math/overview',
      '/math/overview',
      '/math-focus-skills-report',
      '/math/session/:product/:assignmentSessionId/report'
    ],
    tab: SidebarIds.MathContent
  },
  {
    paths: [
      '/rosters',
      '/rosters/archived-roster',
      '/rosters/courses/:courseId/edit',
      '/rosters/create',
      '/rosters/edit',
      '/rosters/login-cards',
      '/students/:studentId/edit'
    ],
    tab: SidebarIds.Rosters
  },
  {
    paths: ['/pricing', '/checkout'],
    tab: SidebarIds.GetPremium
  },
  {
    paths: [
      '/science-units/:scienceUnitId/articles/:articleUuid',
      '/science/reports/writing/:assignmentId',
      '/science/reports/reading/:assignmentId/students/:studentId',
      '/science-units/:pathScienceUnitId',
      '/science-units/:pathScienceUnitId/articles/:articleUuid/assign',
      '/science-units',
      '/science/search',
      '/session-history/science'
    ],
    tab: SidebarIds.ScienceUnits
  },
  {
    paths: [
      '/social-studies-units/:socialStudiesUnitId/articles/:articleUuid',
      '/social-studies/reports/writing/:assignmentId',
      '/social-studies/reports/reading/:assignmentId/students/:studentId',
      '/social-studies-units/:pathSocialStudiesUnitId',
      '/social-studies-units/:pathSocialStudiesUnitId/articles/:articleUuid/assign',
      '/social-studies-units',
      '/social-studies/search'
    ],
    tab: SidebarIds.SocialStudiesUnits
  },
  {
    paths: [
      // These are listed here to prevent errors from logging before route is redirected
      '/courses/*',
      '/default-course/*',
      '/chromeless/*',

      '/teacher-onboarding',
      '/parent-welcome',
      '/mobile-reminder',
      '/referral-program',
      '/settings',
      '/settings/*',
      '/ela-article-debugger',
      '/ela/print/targeted-skills/:assignmentSessionId',
      '/ela/print/articles/:assignmentSessionId',
      '/chromeless-preview'
    ],
    tab: SidebarIds.None
  }
]

export function getTabIdForRoute(routePathName: string): SidebarIdT {
  const match = find(routes, ({paths}) => {
    const matches = matchRoutes(
      paths.map(path => ({path})),
      routePathName
    )
    return matches !== null && matches.length > 0
  })
  if (match === undefined) {
    logError(new Error(`Route "${routePathName}" doesn't have a matching sidebar item`))
    return SidebarIds.None
  }
  return match.tab
}
